import type { CaptchaContext } from '@auth0/auth0-acul-react/types';
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form';

import AuthChallengeWidget from './providers/AuthChallengeWidget';
import FriendlyCaptchaWidget from './providers/FriendlyCaptchaWidget';
import HCaptchaWidget from './providers/HCaptchaWidget';
import RecaptchaCombinedWidget from './providers/ReCaptchaCombinedWidget';
import SimpleCaptchaWidget from './providers/SimpleCaptchaWidget';

// ---
// Interfaces
// ---

export interface CaptchaResponse {
  provider: string;
  token?: string;
  answer?: string;
  arkoseToken?: string;
}

export interface CaptchaWidgetProps<T extends FieldValues = FieldValues> {
  config: {
    provider: string;
    siteKey?: string;
    image?: string;
    size?: string;
    placeholder?: string;
  };
  control?: Control<T>;
  setError?: UseFormSetError<T>;
  clearErrors?: UseFormClearErrors<T>;
  rules?: RegisterOptions<T>;
  name: Path<T>;
  onCaptchaResponse: (response: CaptchaResponse | null) => void;
  onError?: (error: string | undefined) => void;
  className?: string;
  label?: string;
  theme?: 'light' | 'dark' | 'auto';
  error?: string;
}

// Use SDK's CaptchaContext interface
export type ICaptcha = CaptchaContext;

export interface CaptchaProps<T extends FieldValues = FieldValues> {
  captcha?: ICaptcha;
  control: Control<T>;
  setError: UseFormSetError<T>;
  clearErrors: UseFormClearErrors<T>;
  onValidationChange?: (
    isValid: boolean,
    value?: string,
    error?: string,
  ) => void;
  label?: string;
  error?: string;
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
  rules?: RegisterOptions<T>;
  name: Path<T>;
}

// ---
// Main Component
// ---

const Captcha = <T extends FieldValues = FieldValues>({
  rules,
  name,
  captcha,
  control,
  setError,
  clearErrors,
  onValidationChange,
  label,
  error,
  theme,
  className,
}: CaptchaProps<T>) => {
  // ---
  // Constants and Mappings
  // ---

  const CAPTCHA_WIDGET_MAP = {
    recaptcha_v2: RecaptchaCombinedWidget,
    recaptcha_enterprise: RecaptchaCombinedWidget,
    hcaptcha: HCaptchaWidget,
    auth0_v2: AuthChallengeWidget,
    friendly_captcha: FriendlyCaptchaWidget,
  };

  const { provider, image, siteKey } = captcha || {};

  // If no provider is specified, render nothing.
  if (!provider) {
    return null;
  }

  const handleResponse = (res: CaptchaResponse | null) => {
    if (onValidationChange) {
      if (res) {
        const value =
          res.provider === 'auth0' ? res.answer : res.token || res.arkoseToken;
        const isValid = !!value;
        onValidationChange(isValid, value);
      } else {
        onValidationChange(false);
      }
    }
  };

  // Handle the special case for SimpleCaptchaWidget (Auth0 v1)
  if (provider === 'auth0') {
    return image ? (
      <SimpleCaptchaWidget
        config={{ provider: 'auth0', image }}
        onCaptchaResponse={handleResponse}
        control={control}
        name={name}
        rules={rules}
        label={label}
        setError={setError}
        clearErrors={clearErrors}
        error={error}
        className={className}
      />
    ) : null;
  }

  // Use the map for other providers
  const SpecificCaptchaWidget = CAPTCHA_WIDGET_MAP[
    provider as keyof typeof CAPTCHA_WIDGET_MAP
  ] as React.ComponentType<CaptchaWidgetProps<T>>;

  if (SpecificCaptchaWidget && siteKey) {
    return (
      <SpecificCaptchaWidget
        config={{ provider, siteKey }}
        control={control}
        setError={setError}
        clearErrors={clearErrors}
        name={name}
        rules={rules}
        onCaptchaResponse={handleResponse}
        theme={theme}
        label={label}
        error={error}
        className={className}
      />
    );
  }
  return null;
};

export default Captcha;
